import React, { useState } from 'react';
import { useApp } from '../../context/AppContext';
import { aiService } from '../../services/aiService';
import { CHANNEL_META } from '../../utils/helpers';

const CHANNELS = ['linkedin','facebook','instagram','blog','partner'];

export default function ApprovePanel({ ideaId, onClose, onDone }) {
  const { ideas, updateIdea, addContentPieces, voiceState, anthropicKey, anthropicKeyReady, logActivity } = useApp();
  const idea       = ideas.find(i => i.id === ideaId);
  const [selected,   setSelected]   = useState([]);
  const [imgOption,  setImgOption]  = useState('none');
  const [generating, setGenerating] = useState(false);
  const [error,      setError]      = useState('');

  if (!idea) return null;

  function toggleChannel(ch) {
    setSelected(prev => prev.includes(ch) ? prev.filter(c => c !== ch) : [...prev, ch]);
  }

  async function generate() {
    if (selected.length === 0) { setError('Select at least one channel.'); return; }
    if (!anthropicKeyReady) { setError('Add your Anthropic key in Settings → API Keys first.'); return; }
    setGenerating(true);
    setError('');

    // Mark as generating (optimistic)
    await updateIdea(ideaId, { status:'generating', approved_channels: selected }).catch(() => {});

    try {
      const text = await aiService.generateContent({
        voiceProfile: voiceState,
        idea,
        channels:     selected,
        anthropicKey,
      });

      const pieces = selected.map(ch => {
        const regex = new RegExp(`\\[${ch.toUpperCase()}\\]([\\s\\S]*?)(?=\\[[A-Z_]+\\]|$)`, 'i');
        const match = text.match(regex);
        const content = match ? match[1].trim() : text;

        const finishedMatch = content.match(/\[FINISHED POST\]([\s\S]*?)(?=\[TALKING POINTS\]|$)/i);
        const talkingMatch  = content.match(/\[TALKING POINTS\]([\s\S]*?)$/i);

        return {
          idea_id:       ideaId,
          idea_title:    idea.title,
          channel:       ch,
          world:         'business',
          content:       ch === 'partner' ? (finishedMatch ? finishedMatch[1].trim() : content) : content,
          finished_post: ch === 'partner' ? (finishedMatch ? finishedMatch[1].trim() : '') : null,
          talking_points:ch === 'partner' ? (talkingMatch  ? talkingMatch[1].trim()  : '') : null,
          status:        'draft',
          image_option:  imgOption,
        };
      });

      await addContentPieces(pieces);
      await updateIdea(ideaId, { status:'done' });
      logActivity('Generated content', `${selected.length} channels for "${idea.title}"`);
      onDone();
    } catch (err) {
      setError(err.message);
      await updateIdea(ideaId, { status:'new' }).catch(() => {});
    } finally {
      setGenerating(false);
    }
  }

  return (
    <div className="channel-picker" style={{ marginTop:16, marginBottom:8 }}>
      <div className="cp-title">Select channels to generate content for:</div>

      <div className="ch-grid">
        {CHANNELS.map(ch => {
          const meta = CHANNEL_META[ch];
          const on   = selected.includes(ch);
          return (
            <button key={ch} className={`ch-btn ${on ? 'on' : ''}`}
              style={{ borderColor: on ? meta.color : undefined }}
              onClick={() => toggleChannel(ch)}>
              <div className="ch-icon" style={{ background:meta.bg, color:meta.color }}>{meta.emoji}</div>
              <div>
                <div className="ch-label">{meta.label}</div>
                {meta.maxChars && <div className="ch-desc">{meta.maxChars} chars max</div>}
              </div>
            </button>
          );
        })}
      </div>

      <div className="f-lbl" style={{ marginBottom:5 }}>Image option</div>
      <div className="img-opts">
        {[['none','None'],['upload','Upload'],['generate','AI Gen'],['search','Search']].map(([v, label]) => (
          <button key={v} className={`img-opt ${imgOption === v ? 'on' : ''}`} onClick={() => setImgOption(v)}>{label}</button>
        ))}
      </div>

      {error && <div className="err-bar" style={{ marginBottom:10 }}>{error}</div>}

      <div style={{ display:'flex', gap:8 }}>
        <button className="btn btn-outline btn-sm" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary btn-sm" onClick={generate} disabled={generating || selected.length === 0}>
          {generating
            ? <>Generating <span className="dots"><i/><i/><i/></span></>
            : `✨ Generate${selected.length > 0 ? ` (${selected.length})` : ''}`}
        </button>
      </div>
    </div>
  );
}
